import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Briefcase, ExternalLink } from "lucide-react";
import { StatusBadge } from "../../components/Badge";
import type { CalendarEvent } from "../../api/types";

// 日曆上那一格專案,點開的摘要卡(第九輪 Task 4)。
//
// 業主原話:「請假系統 日曆上每一格專案 滑鼠都要可以點進去 然後懸浮視窗
// 出現該專案報價單」;2026-09-13 確認是**摘要卡片 + 一鍵開單**,不列七大類小計 ——
// 他排假時想知道的是「那幾天是什麼案、多大、誰負責」,不是整張明細。
// 真的要看明細就按那顆「開啟報價單」,那才是明細該待的地方。
//
// 三件不能省的事:
//
// 1. **那一格是真的 `<button>`。** 一塊「看起來可以點」的 div 只有滑鼠點得到,
//    鍵盤走不進去、螢幕報讀器也不會說它可以按 —— 而月曆是全公司每個人都在看的頁。
// 2. **卡片的資料全部來自 `/api/calendar/events` 已經帶回來的那一列。**
//    點開一格才去撈一次整張報價單的話,一格月曆上十檔案子就是十次往返,
//    而卡片上只有七行字。
// 3. **portal 到 body + `position: fixed`。** 月曆外層是 `overflow-x-auto`,
//    CSS 規範說 overflow-x 非 visible 時 overflow-y 的 visible 會被算成 auto,
//    絕對定位的浮層會被那個容器裁掉 —— HeaderFilter 與 RowMenu 都踩過這個坑。
//
// **卡片上不畫金額**(業主 2026-09-23:「點開專案後 不用顯示金額 / 顯示
// 統計報價單中 已發包數量 未發包數量」)。排假的人要判斷的是「那幾天有沒有
// 人在盯」,而不是那張單多大。原本那一行總額換成了發包數。

/** 卡片寬度。單號、客戶名稱與那一行發包數都在這個寬度裡讀得完整。 */
const PANEL_WIDTH = 288;
/** 卡片的概估高度,用來判斷要往下開還是往上翻 —— 月曆最後一排就在視窗底部。 */
const PANEL_MAX_HEIGHT = 260;
/** 卡片再怎麼擠也不低於這個高度,比這更矮就讀不出是哪一張單了。 */
const MIN_PANEL_HEIGHT = 180;
/** 卡片與視窗邊緣之間留的空隙。 */
const VIEWPORT_GUTTER = 12;

export interface QuotePopoverProps {
  /** 這一格的專案。卡片上的每一個字都只從這裡來。 */
  event: CalendarEvent;
  /**
   * 檔期的寫法(`12/12 – 12/13`)。由月曆算好傳進來,不在這裡再算一次 ——
   * 格子的提示與卡片上的檔期要是同一份,否則同一個案子會出現兩種寫法。
   */
  range: string;
}

export function QuotePopover({ event, range }: QuotePopoverProps) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelId = `nq-quote-card-${useId()}`;

  /** 依那一格的位置算出卡片的固定座標,下方空間不足時往上翻。 */
  const place = useCallback(() => {
    const anchor = triggerRef.current;
    if (!anchor || typeof window === "undefined" || typeof anchor.getBoundingClientRect !== "function") {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow;
    // 靠左對齊那一格,但不讓卡片掉出視窗外。
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8));
    // 這個方向真正剩多少高度。卡片是 fixed,落在視窗外的那一段捲不進視野 ——
    // 被切掉的「開啟報價單」就是完全按不到,手機直式的月曆很容易踩到。
    const available = (flipUp ? rect.top : window.innerHeight - rect.bottom) - VIEWPORT_GUTTER;
    setStyle({
      position: "fixed",
      left,
      width: PANEL_WIDTH,
      // 視窗比卡片還窄時(手機橫放、分割視窗)寧可讓它縮,也不要橫向撐破畫面。
      maxWidth: "calc(100vw - 1rem)",
      maxHeight: Math.max(MIN_PANEL_HEIGHT, available),
      // 上限之外還要捲得動,否則只是換一種方式把那顆按鈕藏起來。
      overflowY: "auto",
      ...(flipUp ? { bottom: Math.max(8, window.innerHeight - rect.top + 4) } : { top: rect.bottom + 4 }),
    });
  }, []);

  /** 關閉並把焦點還給那一格 —— 鍵盤使用者不能被丟回頁首,月曆有四十二格。 */
  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    place();
    setOpen(true);
  };

  // 開啟後把焦點移到卡片本身(不是卡片裡的連結):報讀器會唸出卡片的名稱,
  // 而滑鼠使用者不會看到連結莫名其妙亮起一圈。
  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
  }, [open]);

  // 開著的時候跟著捲動與縮放走。用捕獲階段才收得到月曆容器自己的橫向捲動。
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  // 點到卡片與那一格以外的地方就收起來。這裡不還焦點:使用者是用滑鼠離開的。
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const onPointerDown = (nativeEvent: MouseEvent) => {
      const target = nativeEvent.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (document.getElementById(panelId)?.contains(target)) return;
      close(false);
    };
    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [open, panelId, close]);

  const onPanelKeyDown = (keyEvent: ReactKeyboardEvent<HTMLDivElement>) => {
    if (keyEvent.key === "Escape" || keyEvent.key === "Tab") {
      // 卡片是浮在月曆上的,Tab 走過「開啟報價單」再一次就離開卡片,落在被它蓋住的
      // 格子上;而 Escape 綁在卡片本身,焦點一出去就按不動它。兩個鍵都直接收起來。
      keyEvent.preventDefault();
      close();
    }
  };

  const card =
    open && style !== null && typeof document !== "undefined"
      ? createPortal(
          <div
            id={panelId}
            ref={panelRef}
            role="dialog"
            aria-label={`專案摘要 ${event.quote_no}`}
            tabIndex={-1}
            style={style}
            onKeyDown={onPanelKeyDown}
            className={
              // 焦點環留著:卡片開啟時焦點就落在它身上,那一圈正是「現在在這裡」的
              // 唯一指示,關掉的話鍵盤使用者按下 Esc 前完全不知道自己在哪。
              "z-50 flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] " +
              "bg-[var(--surface)] p-3 shadow-card"
            }
          >
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex min-w-0 items-center gap-1 text-xs font-semibold text-[var(--text-2)]">
                <Briefcase aria-hidden="true" size={12} className="shrink-0 text-[var(--accent)]" />
                <span className="truncate">{event.quote_no}</span>
              </span>
              {/* 狀態一律「顏色 + 文字」,徽章自己帶中文,不可只靠顏色。 */}
              <StatusBadge status={event.status} />
            </div>

            <p className="m-0 text-sm font-semibold leading-snug text-[var(--text)]">{event.title}</p>

            <dl className="m-0 flex flex-col gap-1 text-xs">
              {/* 還沒指定客戶、還沒派人都是常態(先開單後派人),寫出來比留白好 ——
                  一片空白會讓人以為卡片壞了,而不是「這件事還沒決定」。 */}
              <Row label="客戶" value={event.client_name || "未指定"} muted={event.client_name === ""} />
              <Row label="檔期" value={range} />
              {/* 業主 2026-09-23:「點開專案後 不用顯示金額 / 顯示 統計報價單中
                  已發包數量 未發包數量」。排假的人要判斷的是「那幾天有沒有人
                  在盯」,不是那張單多大。

                  **三個狀態都列**(業主選的)。「待確認」併進未發包的話,
                  已經跟廠商講好、只差沒回覆的那幾類就看不出來了。

                  數的是報價分類,不是明細列 —— 業主是以分類為單位在發包的
                  (見 models.OutsourceStatus)。 */}
              <Row
                label="發包"
                value={`已發包 ${event.outsource_ordered}・未發包 ${event.outsource_not_ordered}・待確認 ${event.outsource_pending}`}
                strong
              />
              <Row label="負責同仁" value={event.owner_name || "未指派"} muted={event.owner_name === ""} />
            </dl>

            {/* 一鍵開單。做成真的連結而不是按鈕:中鍵、Ctrl+點擊開新分頁都通,
                排假的人常常是「先開起來看,月曆那邊不要關掉」。 */}
            <Link
              to={`/quotes/${event.quote_id}`}
              className={
                "inline-flex items-center justify-center gap-1 rounded-[var(--radius)] border " +
                "border-[var(--accent)] bg-[var(--accent)] px-2 py-1.5 text-xs font-medium " +
                "text-[var(--on-accent)] hover:bg-[var(--accent-hover)]"
              }
            >
              <ExternalLink aria-hidden="true" size={12} />
              開啟報價單
            </Link>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        // 無障礙名稱把那一條在月曆上的意思講完整:哪一個案、哪幾天。
        // 報讀器逐格唸過去時,「按鈕」兩個字之外還得知道按下去是什麼。
        aria-label={`專案 ${event.quote_no} ${event.title},${range}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={toggle}
        className={
          "flex w-full min-w-0 items-center gap-1 overflow-hidden rounded-[var(--radius)] " +
          "border-l-4 border-[var(--accent)] bg-[var(--accent-soft)] px-1 py-0.5 text-left " +
          "text-[11px] leading-tight text-[var(--accent-hover)] hover:bg-[var(--accent-soft)] " +
          "hover:brightness-95"
        }
      >
        <Briefcase aria-hidden="true" size={11} className="shrink-0" />
        {/* 格子裡寫**專案名稱**,不寫單號(業主 2026-09-30:「不要顯示專案序號 而是顯示專案名稱」)。
            以前單號排在前面,一格的寬度剛好被它吃掉,名稱只剩兩三個字。
            單號還在:滑鼠移上去的提示、報讀器唸的名字、點開的摘要卡片都有。
            「專案」兩個字 2026-10-15 拿掉(業主),公事包圖示與顏色已經分得出來。 */}
        <span className="truncate">{event.title || event.quote_no}</span>
      </button>
      {card}
    </>
  );
}

interface RowProps {
  label: string;
  value: string;
  /** 金額那一列要看得出比別列重。 */
  strong?: boolean;
  /** 還沒填的值用弱一階的顏色,但仍然是文字,不是空白。 */
  muted?: boolean;
}

function Row({ label, value, strong = false, muted = false }: RowProps) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="shrink-0 text-[var(--text-3)]">{label}</dt>
      <dd
        className={
          "m-0 min-w-0 truncate text-right " +
          (strong ? "text-sm font-semibold text-[var(--text)] " : "") +
          (muted ? "text-[var(--text-3)]" : strong ? "" : "text-[var(--text-2)]")
        }
      >
        {value}
      </dd>
    </div>
  );
}
